import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Mail, Eye, Users, TrendingUp, UserPlus, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCustomers = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [customersRes, statsRes] = await Promise.all([
          axios.get(`${API}/api/customers/`),
          axios.get(`${API}/api/customers/stats/summary`)
        ]);
        
        if (isMounted) {
          setCustomers(customersRes.data.customers || []);
          setStats(statsRes.data);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const viewCustomer = async (customerId) => {
    try {
      const response = await axios.get(`${API}/api/customers/${customerId}`);
      setSelectedCustomer(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-charcoal font-heading">Customers</h1>
            <p className="text-stone-grey font-body">{customers.length} total customers</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-6 border border-soft-sand shadow-soft">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-grey font-body">Total Customers</p>
                  <p className="text-2xl font-bold text-charcoal font-heading">{stats.total_customers}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6 border border-soft-sand shadow-soft">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <UserPlus className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-grey font-body">New This Month</p>
                  <p className="text-2xl font-bold text-charcoal font-heading">{stats.new_this_month}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6 border border-soft-sand shadow-soft">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-grey font-body">With Orders</p>
                  <p className="text-2xl font-bold text-charcoal font-heading">{stats.customers_with_orders}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-lg p-6 border border-soft-sand shadow-soft">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-grey" />
            <Input
              type="text"
              placeholder="Search customers by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 font-body"
            />
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-lg border border-soft-sand shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-soft-sand">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Customer</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Orders</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Total Spent</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Loyalty Points</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Joined</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-charcoal font-body">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-soft-sand">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Users className="h-12 w-12 text-stone-grey mx-auto mb-4 opacity-50" />
                      <p className="text-stone-grey font-body">No customers found</p>
                      <p className="text-sm text-stone-grey/70 font-body mt-1">
                        Customers will appear here when they register
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-soft-sand/30 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-charcoal font-body">{customer.name}</p>
                          <p className="text-sm text-stone-grey font-body">{customer.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-grey font-body">{customer.orders || 0}</td>
                      <td className="px-6 py-4 font-medium text-charcoal font-body">
                        ${(customer.total_spent || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-green font-body">
                        {customer.loyalty_points || 0} pts
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-grey font-body">{customer.joined}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-charcoal hover:bg-soft-sand"
                            onClick={() => viewCustomer(customer.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-charcoal hover:bg-soft-sand"
                            onClick={() => {
                              window.open(`mailto:${customer.email}`, '_blank');
                            }}
                            title={`Send email to ${customer.email}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Customer Detail Modal */}
        {showModal && selectedCustomer && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-soft-sand">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-charcoal font-heading">Customer Details</h2>
                  <Button variant="ghost" onClick={() => setShowModal(false)}>
                    ✕
                  </Button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Customer Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-stone-grey font-body">Name</p>
                    <p className="font-medium text-charcoal font-body">
                      {selectedCustomer.first_name} {selectedCustomer.last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-grey font-body">Email</p>
                    <p className="font-medium text-charcoal font-body">{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-grey font-body">Phone</p>
                    <p className="font-medium text-charcoal font-body">{selectedCustomer.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-grey font-body">Member Since</p>
                    <p className="font-medium text-charcoal font-body">
                      {selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Recent Orders */}
                <div>
                  <h3 className="font-semibold text-charcoal font-heading mb-3">Recent Orders</h3>
                  {selectedCustomer.orders?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedCustomer.orders.slice(0, 5).map((order) => (
                        <div key={order._id} className="flex items-center justify-between p-3 bg-soft-sand/50 rounded-lg">
                          <div>
                            <p className="font-medium text-charcoal font-body">
                              Order #{order.order_number || order._id.slice(-6)}
                            </p>
                            <p className="text-sm text-stone-grey font-body">
                              {order.items?.length || 0} items
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-charcoal font-body">${order.total?.toFixed(2)}</p>
                            <p className="text-sm text-stone-grey font-body capitalize">{order.status}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-grey font-body">No orders yet</p>
                  )}
                </div>

                {/* Loyalty Info */}
                {selectedCustomer.loyalty && (
                  <div>
                    <h3 className="font-semibold text-charcoal font-heading mb-3">Loyalty Program</h3>
                    <div className="p-4 bg-muted-green/10 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-charcoal font-body">
                            {selectedCustomer.loyalty.tier || 'Bronze'} Member
                          </p>
                          <p className="text-sm text-muted-green font-body">
                            {selectedCustomer.loyalty.points || 0} points available
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCustomers;
